import { useState } from 'react'
import {
  AuthError,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  getMultiFactorResolver,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider
} from 'firebase/auth'
import { auth } from '..'
import { useAuth } from '../providers/auth'
import { useTheme } from '../providers/theme'
import makeToast from '../components/toast'

interface ErrorTypes {
  email?: string
  password?: string
  other?: string
}

function authError(errorCode: string) {
  switch (errorCode) {
    case 'auth/invalid-email':
      return { email: 'You have not entered a valid email' }
    case 'auth/user-not-found':
      return { email: 'Email could not be found' }
    case 'auth/missing-email':
      return { email: 'You must enter an email' }
    default:
      return { other: 'Something went wrong, please try again' }
  }
}

export function useSignInEmail(): [
  (email: string, password: string) => void,
  boolean,
  ErrorTypes | null,
  () => void
] {
  const {loading, setLoading} = useAuth()
  const {mode} = useTheme()
  const [error, setError] = useState<ErrorTypes | null>(null)

  const clearError = () => setError(null)

  const signInEmail = async (email: string, password: string) => {
    try {
      signInWithEmailAndPassword(auth, email, password).then(userCredential => {
        console.log("User signed in, no second-factor");
      }).catch(error => {
        if (error.code === 'auth/multi-factor-auth-required') {
          const resolver = getMultiFactorResolver(auth, error)
          if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
            const phoneInfoOptions = {
              multiFactorHint: resolver.hints[0],
              session: resolver.session
            }
            const phoneAuthProvider = new PhoneAuthProvider(auth)
            // Send SMS verification code
            return phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, (window as any).recaptchaVerifier).then(verificationId => {
              // Ask user for the SMS verification code. Then:
              const verificationCode = prompt("Enter the verification code we sent to your number:")
              if (verificationCode !== null) {
                const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
                const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
                // Complete sign-in.
                resolver.resolveSignIn(multiFactorAssertion).then((userCredential) => {
                  // User successfully signed in with the second factor phone number.
                  console.log("User signed in with multi factor");
                  return userCredential;
                }).catch(error => {
                  console.error(error)
                  makeToast('error', 'Invalid verification code.', mode)
                })
              } else {
                alert("Invalid verification code.");
                return null;
              }
            })
          }
        } else if (error.code === 'auth/wrong-password') {
          makeToast('error', 'Wrong password.', mode)
        } else {
          makeToast('error', 'Something went wrong.', mode)
        }
      });
    } catch (error) {
      setError(authError((error as AuthError).code))
      setLoading(false)
    }
  }

  return [signInEmail, loading, error, clearError]
}

export function useResetPassword(): [
  (email: string) => void,
  boolean,
  ErrorTypes | null,
  () => void
] {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorTypes | null>(null)

  const clearError = () => setError(null)

  const sendResetEmail = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email)
      setLoading(false)
    } catch (e) {
      const error = e as AuthError
      setError(authError(error.code))
      setLoading(false)
    }
  }

  return [sendResetEmail, loading, error, clearError]
}

export function useRegisterEmail(): [
  (email: string, password: string) => void, 
  boolean, 
  ErrorTypes | null,
  () => void
] {
  const {loading, setLoading} = useAuth()
  const [error, setError] = useState<ErrorTypes | null>(null)

  const clearError = () => setError(null)

  const registerEmail = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password)
      sendEmailVerification(auth.currentUser!)
      setLoading(false)
    } catch (e) {
      const err = e as AuthError
  
      switch (err.code) {
        case 'auth/invalid-email':
          setError({ email: 'You have not entered a valid email' })
          break
  
        case 'auth/user-not-found':
          setError({ email: 'Email could not be found' })
          break
  
        default:
          setError({ email: 'Something went wrong, please try again' })
      
      }
      
      setLoading(false)
    }
  }

  return [registerEmail, loading, error, clearError]
}

export function useSignInGoogle(): [() => void, boolean] {
  const {loading, setLoading} = useAuth()

  const signInGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider()
      provider.addScope('profile')
      provider.addScope('email')
      await signInWithPopup(auth, provider)
    } catch (_) {
      setLoading(false)
    }
  }

  return [signInGoogle, loading]
}